import React, { useCallback, useEffect, useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { clone, equals, mergeLeft, omit, pickBy, pipe } from "ramda";
import {
  useContent,
  useContentUpdate,
  useUploadContentThumbnail,
} from "../contentServices";
import { anyTrue, isBlank, notBlank } from "../../../util/ext";
import {
  setPurchaseLink,
  setPubDate,
  setProductIdentifiers,
} from "../../../util/mediaUtil";
import {
  optionalContentFieldsMapper,
  formatOptionalFields,
  getOptionalFields,
} from "./optionalContentFieldsUtils";
import {
  validateContent,
  validateContentField,
  hasError,
  firstError,
  validateProductIdentifiers,
} from "../contentValidation";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { MediaContext } from "../../common/media-context";
import { useConfirm } from "../../../common/use-confirm";
import { useIsAdmin, useIsUserIAPManager } from "../../../auth/authUtils";
import { Link as RouterLink, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/pro-regular-svg-icons";
import clsx from "clsx";
import { useMediaQuery } from "@material-ui/core";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import Container from "@material-ui/core/Container";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";

import ConfirmDialog from "../../../components/ConfirmDialog";
import ContentMediaActions from "./ContentMediaActions";
import ContentBadges from "./ContentBadges";
import EditCategoriesColumn from "../../common/EditCategoriesColumn";
import EditContentDetailsColumn from "./EditContentDetailsColumn";
import EditContentInfoColumn from "./EditContentInfoColumn";
import EditContentOptionalColumn from "./EditContentOptionalColumn";
import EditMediaStatusColumn from "./EditMediaStatusColumn";
import EditContributorsColumn from "./EditContributorsColumn";
import EditDescriptionColumn from "../../common/EditDescriptionColumn";
import EditThumbnailOverlay from "../../common/EditThumbnailOverlay";
import IapSelectCheckbox from "./IapSelectCheckbox";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import SnackAlert from "../../../components/SnackAlert";
import ProductIdentifiers from "./ProductIdentifiers";
import SaveButton from "../../../components/SaveButton";

const styles = theme => ({
  root: {
    padding: "40px 0 0 0",
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },

  dialogRoot: {
    padding: "0 50px",
  },

  header: {
    margin: 0,
    padding: "40px 0 20px 0",
  },

  closeButton: {
    color: theme.palette.primary.darker,
    fontWeight: "bolder",
  },

  closeButtonIcon: {
    fontSize: "3.2rem",
  },

  loadingContainer: {
    padding: theme.spacing(2, 5),
    height: "100%",
  },

  scrollArea: {
    height: "calc(100vh - 300px)",
    overflowY: "auto",
  },

  editPanel: {
    height: "100%",
    width: "calc(100% - 15px)",
  },

  info: {
    padding: 0,
  },

  required: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(3),
  },

  actions: {
    paddingTop: 20,
    paddingBottom: 70,
  },

  column: {
    display: "flex",
    flexGrow: 1,
    flexBasis: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: 24,
  },

  columnRight: {
    marginTop: 32,
  },

  columnRightInner: {
    flexDirection: "row",
    [theme.breakpoints.up("md")]: {
      flexDirection: "column",
    },
  },

  gap: {
    gap: 24,
  },

  loader: {
    color: "white !important",
  },
});

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)(props => {
  const { children, classes, onClose, type, ...other } = props;
  const { id } = useParams();
  const isAdmin = useIsAdmin();
  const isIAPManager = useIsUserIAPManager();

  const [showIAPCheckbox, setShowIAPCheckbox] = useState(false);
  useEffect(() => {
    setShowIAPCheckbox(
      equals(type, "BOOK") && anyTrue([isAdmin, isIAPManager]),
    );
  }, [type, isAdmin, isIAPManager]);

  return (
    <MuiDialogTitle disableTypography className={classes.header} {...other}>
      <Grid container justify="space-between" alignItems="center">
        <Grid container alignItems="center" item xs={6}>
          <Grid item>
            <Box minWidth={150}>
              <Typography variant="h6">{children}</Typography>
            </Box>
          </Grid>

          <Grid item>
            <ContentMediaActions />
          </Grid>
        </Grid>

        <Grid
          container
          item
          justify="flex-end"
          alignItems="center"
          xs={6}
          spacing={2}
        >
          {isAdmin && notBlank(id) && (
            <Grid item>
              <Button
                component={RouterLink}
                to={`/creator/manage-access/content/${id}`}
                startIcon={<FontAwesomeIcon icon={faKey} />}
              >
                Manage Access
              </Button>
            </Grid>
          )}

          {showIAPCheckbox && (
            <Grid item xs={3} xl={2}>
              <IapSelectCheckbox />
            </Grid>
          )}

          {isAdmin && (
            <Grid item>
              <EditMediaStatusColumn />
            </Grid>
          )}

          {onClose ? (
            <Grid item>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onClose}
              >
                <CloseIcon className={classes.closeButtonIcon} />
              </IconButton>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </MuiDialogTitle>
  );
});

const objectDiff = media => (val, key) => !equals(media[key], val);

const validator = {
  validateField: validateContentField,
  hasError: hasError,
  firstError: firstError,
};

const ContentEditDialog = props => {
  const { visible, onCancel, id } = props;
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const isIAPManager = useIsUserIAPManager();

  const isMd = useMediaQuery(theme => theme.breakpoints.up("md"));

  //-- Manage the alert message
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  //-- Set up overall state
  const [content, setContent] = useState({});
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [initialContent, setInitialContent] = useState({});
  const [optionalFields, setOptionalFields] = useState([]);
  const [initialOptionalFields, setInitialOptionalFields] = useState([]);

  const result = useContent(id);
  useEffect(() => {
    if (result.isSuccess) {
      setContent(result.data);
      setInitialContent(clone(result.data));

      const formattedOptionalFields = optionalContentFieldsMapper(
        result.data.type,
      );

      const optionalFields = getOptionalFields(
        formattedOptionalFields,
        result.data,
        isAdmin,
      );

      setOptionalFields(optionalFields);
      setInitialOptionalFields(clone(optionalFields));
    }
  }, [isAdmin, result.isSuccess, result.data]);

  //-- Set up the thumbnail uploader
  const thumbnailUpload = useUploadContentThumbnail();

  //-- Handle saving the content
  const updateContent = useContentUpdate();
  const handleSave = useCallback(async () => {
    const validation = mergeLeft(
      validateProductIdentifiers(content.productIdentifiers),
      validateContent(content.type, content),
    );
    console.log("validation: ");
    console.log(validation);
    const errors = isBlank(validation) ? {} : validation;
    setErrors(errors);

    if (isBlank(errors)) {
      try {
        const formattedOptionalFields = formatOptionalFields(optionalFields);
        const formattedInitialOptionalFields = formatOptionalFields(
          initialOptionalFields,
        );

        const optionalFieldsUpdate = pickBy(
          objectDiff(formattedInitialOptionalFields),
          formattedOptionalFields,
        );

        const initialUpdate = pickBy(objectDiff(initialContent), content);

        const update = pipe(
          mergeLeft(optionalFieldsUpdate),
          setPurchaseLink,
          setPubDate,
          setProductIdentifiers,
        )(initialUpdate);

        await updateContent.mutateAsync({
          resourceIds: { id: initialContent.id },
          data: omit(["publicationYear"], update),
        });

        setInitialContent(content);
        setInitialOptionalFields(optionalFields);
        showAlert("success", ["Content Successfully Saved"]);
      } catch (err) {
        console.log(err);
        showAlert("error", [err.message]);
      }
    } else {
      showAlert("error", [
        "There are errors that should be fixed before saving.",
      ]);
    }
  }, [
    initialContent,
    content,
    updateContent,
    showAlert,
    optionalFields,
    initialOptionalFields,
  ]);

  const handleCloseClicked = useCallback(() => {
    const update = pickBy(objectDiff(initialContent), content);

    if (isBlank(update)) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have made changes to this content without saving.  Do you wish to discard these changes and go back to the content list?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [content, initialContent, showConfirm, onCancel]);

  const [saveDisabled, setSaveDisabled] = useState(true);
  useEffect(() => {
    const optionalFieldsUpdate = pickBy(
      objectDiff(formatOptionalFields(initialOptionalFields)),
      formatOptionalFields(optionalFields),
    );
    const update = pickBy(objectDiff(initialContent), content);
    if (notBlank(update) || notBlank(optionalFieldsUpdate)) {
      setSaveDisabled(false);
    }
  }, [content, initialContent, optionalFields, initialOptionalFields]);

  return (
    <Dialog
      fullScreen
      classes={{ paper: classes.dialogRoot }}
      open={visible}
      onClose={handleCloseClicked}
    >
      {result.isSuccess ? (
        <MediaContext.Provider
          value={{
            initial: initialContent,
            media: content,
            setMedia: setContent,
            errors: errors,
            setErrors: setErrors,
            thumbnailUrl: thumbnailUrl,
            setThumbnailUrl: setThumbnailUrl,
            optionalFields: optionalFields,
            setOptionalFields: setOptionalFields,
            requiredFields: {},
            isUpload: false,
            validator: validator,
          }}
        >
          <DialogTitle
            id="edit-content-dialog-title"
            onClose={handleCloseClicked}
            type={content.type}
          >
            Edit Metadata
          </DialogTitle>

          <Divider />

          <DialogContent id="content-edit" classes={{ root: classes.root }}>
            <SnackAlert handler={snackAlertHandler} />
            <ConfirmDialog handler={confirmHandler} />

            <Container
              id="content-edit-container"
              maxWidth={false}
              disableGutters
            >
              <div className={classes.scrollArea}>
                <Grid
                  container
                  spacing={10}
                  id="content-edit-panel"
                  className={classes.editPanel}
                >
                  <Grid item xs={5} md={4} className={classes.column}>
                    <Grid item>
                      <EditThumbnailOverlay
                        accept="jpg, jpeg, image/jpeg, image/jpg"
                        showAlert={showAlert}
                        uploader={thumbnailUpload}
                        hasThumbnailLock
                      />
                    </Grid>

                    <EditContentDetailsColumn />

                    <EditContentInfoColumn className={classes.column} />
                  </Grid>

                  <Grid
                    item
                    container
                    xs={7}
                    md={8}
                    className={clsx(classes.column, classes.columnRight)}
                  >
                    <Grid item>
                      <EditDescriptionColumn enableLinks required />
                    </Grid>

                    <Grid
                      item
                      container
                      className={clsx(classes.columnRightInner, classes.gap)}
                    >
                      <Grid
                        container
                        item
                        xs={12}
                        md={6}
                        style={{ paddingRight: isMd ? "12px" : 0 }}
                        className={clsx(classes.columnRightInner, classes.gap)}
                      >
                        <Grid item xs={12} md>
                          <EditCategoriesColumn
                            showAudience
                            validator={validator}
                            className={classes.column}
                          />
                        </Grid>

                        <Grid item xs={12} md>
                          <EditContributorsColumn />
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        item
                        xs={12}
                        md={6}
                        className={classes.gap}
                      >
                        <Grid item xs={12}>
                          <ProductIdentifiers />
                        </Grid>

                        <Grid item xs={12}>
                          <EditContentOptionalColumn
                            className={classes.column}
                          />
                        </Grid>

                        {anyTrue([isAdmin, isIAPManager]) && notBlank(content) && (
                          <Grid item xs={12}>
                            <ContentBadges className={classes.column} />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </Container>
          </DialogContent>

          <Divider />

          <DialogActions classes={{ root: classes.actions }}>
            <Button onClick={handleCloseClicked}>Cancel</Button>
            <SaveButton
              loading={updateContent.isLoading}
              disabled={saveDisabled}
              onClick={handleSave}
            />
          </DialogActions>
        </MediaContext.Provider>
      ) : (
        <div className={classes.loadingContainer}>
          <PanelLoadingIndicator />
        </div>
      )}
    </Dialog>
  );
};

export default ContentEditDialog;
